import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getCurrentCounselorProfile,
  updateCurrentCounselorProfile,
  getCounselorProfile,
  updateCounselorProfile,
  updateCounselorAvailability,
  getCounselorStats,
  getConnectedStudents,
  getPendingRequests,
  getCounselorSessionHistory
} from '@/services/counselor'
import type { CounselorProfile } from '@/types'

// Query keys
export const counselorKeys = {
  all: ['counselor'] as const,
  currentProfile: () => [...counselorKeys.all, 'current-profile'] as const,
  profile: (userId: string) => [...counselorKeys.all, 'profile', userId] as const,
  stats: (userId: string, timeframe?: string) => [...counselorKeys.all, 'stats', userId, timeframe] as const,
  connectedStudents: (userId: string, params?: any) => [...counselorKeys.all, 'connected-students', userId, params] as const,
  pendingRequests: (userId: string) => [...counselorKeys.all, 'pending-requests', userId] as const,
  sessionHistory: (userId: string, params?: any) => [...counselorKeys.all, 'session-history', userId, params] as const
}

// Get current counselor profile (for authenticated counselor)
export const useCurrentCounselorProfile = () => {
  return useQuery({
    queryKey: counselorKeys.currentProfile(),
    queryFn: getCurrentCounselorProfile,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Update current counselor profile
export const useUpdateCurrentCounselorProfile = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: Partial<CounselorProfile>) =>
      updateCurrentCounselorProfile(data),
    onSuccess: (data) => {
      // Update the current profile cache
      queryClient.setQueryData(counselorKeys.currentProfile(), data)
    },
  })
}

// Get counselor profile by ID (for admin use)
export const useCounselorProfile = (userId?: string) => {
  return useQuery({
    queryKey: counselorKeys.profile(userId || ''),
    queryFn: () => getCounselorProfile(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Update counselor profile
export const useUpdateCounselorProfile = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: Partial<CounselorProfile> }) =>
      updateCounselorProfile(userId, data),
    onSuccess: (data, variables) => {
      // Update the profile cache
      queryClient.setQueryData(counselorKeys.profile(variables.userId), data)

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: counselorKeys.stats(variables.userId) })
    },
  })
}

// Update counselor availability
export const useUpdateCounselorAvailability = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, isAvailable }: { userId: string; isAvailable: boolean }) =>
      updateCounselorAvailability(userId, isAvailable),
    onSuccess: (_, variables) => {
      // Invalidate profile to refetch updated availability
      queryClient.invalidateQueries({ queryKey: counselorKeys.profile(variables.userId) })
    },
  })
}

// Get counselor statistics
export const useCounselorStats = (userId?: string, timeframe?: 'today' | 'week' | 'month' | 'all') => {
  return useQuery({
    queryKey: counselorKeys.stats(userId || '', timeframe),
    queryFn: () => getCounselorStats(userId!, timeframe),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

// Get connected students
export const useConnectedStudents = (userId?: string, params?: any) => {
  return useQuery({
    queryKey: counselorKeys.connectedStudents(userId || '', params),
    queryFn: () => getConnectedStudents(userId!, params),
    enabled: !!userId,
    staleTime: 1 * 60 * 1000, // 1 minute
  })
}

// Get pending requests
export const usePendingRequests = (userId?: string) => {
  return useQuery({
    queryKey: counselorKeys.pendingRequests(userId || ''),
    queryFn: () => getPendingRequests(userId!),
    enabled: !!userId,
    refetchInterval: 30 * 1000, // Refetch every 30 seconds for real-time updates
    staleTime: 10 * 1000, // 10 seconds
  })
}

// Get session history
export const useCounselorSessionHistory = (userId?: string, params?: any) => {
  return useQuery({
    queryKey: counselorKeys.sessionHistory(userId || '', params),
    queryFn: () => getCounselorSessionHistory(userId!, params),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}